import {
  ArrowRight,
  Building2,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

import {
  Button,
  ButtonLink,
  StatusPill,
} from "../../design/primitives";

type AuthenticationGateProps = {
  state: "AUTHENTICATION_REQUIRED" | "UNAVAILABLE";
  correlationId: string | null;
  retrying: boolean;
  onRetry: () => void;
};

const assurances = [
  {
    label: "Identitate",
    value: "Persoana este confirmată de directorul organizației.",
  },
  {
    label: "Drepturi",
    value: "Rolurile sunt reverificate pentru sesiunea curentă.",
  },
  {
    label: "Dovadă",
    value: "Acțiunile autorizate primesc o corelație de audit.",
  },
] as const;

export const AuthenticationGate = ({
  state,
  correlationId,
  retrying,
  onRetry,
}: AuthenticationGateProps) => {
  const authenticationRequired = state === "AUTHENTICATION_REQUIRED";

  return (
    <main className="access-gate">
      <a className="skip-link" href="#access-action">
        Sari la acces
      </a>

      <section
        className="access-gate__context"
        aria-labelledby="access-heading"
      >
        <div className="brand-lockup access-gate__brand">
          <span className="brand-mark" aria-hidden="true">
            G
          </span>
          <span>
            <strong>GESEIDL</strong>
            <small>Portal AI organizațional</small>
          </span>
        </div>

        <div className="access-gate__statement">
          <p className="eyebrow eyebrow--dark">Acces controlat</p>
          <h1 id="access-heading">
            Lucrul începe cu identitatea organizațională.
          </h1>
          <p>
            Portalul atribuie cererile persoanei autentificate și aplică
            drepturile aprobate înainte de orice lucru cu un model.
          </p>
        </div>

        <dl
          className="access-gate__assurances"
          aria-label="Controalele accesului"
        >
          {assurances.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>

        <p className="access-gate__context-note">
          Identitate personală · drepturi organizaționale · audit
          metadata-only
        </p>
      </section>

      <section
        className="access-gate__action"
        id="access-action"
        aria-labelledby="access-action-heading"
      >
        <div
          className={
            authenticationRequired
              ? "access-gate__symbol"
              : "access-gate__symbol access-gate__symbol--error"
          }
          aria-hidden="true"
        >
          {authenticationRequired ? (
            <ShieldCheck />
          ) : (
            <ShieldAlert />
          )}
        </div>

        <div
          className="access-gate__status"
          role={authenticationRequired ? "status" : "alert"}
          aria-atomic="true"
        >
          <StatusPill
            tone={authenticationRequired ? "info" : "error"}
          >
            {authenticationRequired
              ? "Autentificare necesară"
              : "Portal indisponibil"}
          </StatusPill>
        </div>

        <div className="access-gate__action-copy">
          <h2 id="access-action-heading">
            {authenticationRequired
              ? "Continuă cu identitatea GESEIDL"
              : "Conexiunea sigură nu a putut fi confirmată"}
          </h2>
          <p>
            {authenticationRequired
              ? (
                  "Vei continua în serviciul de identitate al "
                  + "organizației. Portalul nu cere și nu păstrează "
                  + "parola în această pagină. Utilizatorul organizațional "
                  + "conectat este preluat automat; formularul central "
                  + "apare numai dacă organizația nu poate confirma acea "
                  + "identitate."
                )
              : (
                  "Autentificarea nu este afișată când răspunsul "
                  + "portalului nu poate fi validat. Reîncearcă "
                  + "verificarea înainte de a continua."
                )}
          </p>
        </div>

        {authenticationRequired ? (
          <ButtonLink
            className="access-gate__primary-action"
            href="/auth/login"
          >
            <Building2 size={18} aria-hidden="true" />
            <span>Folosește utilizatorul organizațional curent</span>
            <ArrowRight size={18} aria-hidden="true" />
          </ButtonLink>
        ) : (
          <Button
            className="access-gate__primary-action"
            busy={retrying}
            onClick={onRetry}
          >
            <RotateCcw size={18} aria-hidden="true" />
            {retrying ? "Verificăm din nou" : "Reîncearcă verificarea"}
          </Button>
        )}

        <p className="access-gate__action-note">
          {authenticationRequired
            ? "Nu se trimite nicio cerere către furnizorii AI înainte de autentificare."
            : "Nicio sesiune și nicio cerere AI nu au fost pornite."}
        </p>

        {correlationId ? (
          <dl className="access-gate__reference">
            <div>
              <dt>Referință diagnostic</dt>
              <dd className="is-mono">{correlationId}</dd>
            </div>
          </dl>
        ) : null}
      </section>
    </main>
  );
};
